import { 
  checkTaskUsage, 
  checkPageUsage, 
  recordTaskUsage, 
  recordPageUsage 
} from '../slice/usageSlice';
import * as pdfjsLib from 'pdfjs-dist';

// Ensure pdfjs worker is set up
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

// Function to check if user can start a task
export const canStartTask = async (dispatch, organization_id, task_id) => {
  try {
    const result = await dispatch(checkTaskUsage({ organization_id, task_id })).unwrap();
    return { canProceed: true, data: result };
  } catch (error) {
    return { 
      canProceed: false, 
      error: error.error || "Die für diese Aufgabe festgelegten Limits sind erreicht. Bitte wenden Sie sich an Ihren Administrator, um ein Abonnement mit höheren Limits einzurichten." 
    };
  }
};

// Function to check if user can upload pages
export const canUploadPages = async (dispatch, organization_id, history_id, total_pages, task_id) => {
  try {
    console.log('[usageChecker][canUploadPages] payload:', { organization_id, history_id, total_pages, task_id });
    const result = await dispatch(checkPageUsage({ organization_id, history_id, total_pages, task_id })).unwrap();
    console.log('[usageChecker][canUploadPages] result:', result);
    // Expecting { can_proceed, remaining_pages, max_pages }
    const remaining = typeof result?.remaining_pages === 'number' ? result.remaining_pages : 0;
    const maxPages = typeof result?.max_pages === 'number' ? result.max_pages : 0;
    const allowed = !!result?.can_proceed && total_pages <= remaining;
    return {
      canProceed: allowed,
      data: { remaining_pages: remaining, max_pages: maxPages, requested_pages: total_pages },
      error: allowed ? null : `Page limit exceeded: requested ${total_pages}, remaining ${remaining}, max ${maxPages}`,
    };
  } catch (error) {
    console.log('[usageChecker][canUploadPages] error:', error);
    return { 
      canProceed: false, 
      error: error.error || 'Failed to check page usage' 
    };
  }
};

// Function to record task usage after successful start
export const recordTaskStarted = async (dispatch, organization_id, task_id) => {
  try {
    await dispatch(recordTaskUsage({ organization_id, task_id })).unwrap();
    return true;
  } catch (error) {
    console.error('Failed to record task usage:', error);
    return false;
  }
};

// Function to record page usage after successful upload
export const recordPagesUploaded = async (dispatch, organization_id, history_id, total_pages, task_id) => {
  try {
    console.log('[usageChecker][recordPagesUploaded] payload:', { organization_id, history_id, total_pages, task_id });
    const res = await dispatch(recordPageUsage({ organization_id, history_id, total_pages, task_id })).unwrap();
    console.log('[usageChecker][recordPagesUploaded] result:', res);
    return true;
  } catch (error) {
    console.error('Failed to record page usage', error);
    return false;
  }
};

// Function to count PDF pages from file list
export const countPDFPages = async (files) => {
  let totalPages = 0;

  for (const file of files) {
    if (file.type === 'application/pdf') {
      try {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        totalPages += pdf.numPages;
        await pdf.destroy(); // Clean up to free memory
      } catch (error) {
        console.error(`Error counting pages for ${file.name}:`, error);
        throw new Error(`Failed to process ${file.name}`);
      }
    }
  }

  return totalPages;
};

// Function to get total pages from already uploaded files
export const getTotalPagesFromUploadedFiles = (userFiles) => {
  // Note: This function currently cannot accurately count pages because userFiles.files
  // only contains metadata (e.g., filename, file_size) and not the actual file blobs.
  // To get accurate page counts, you should fetch page counts from the server
  // where the files are stored, or store page counts in userFiles metadata.
  console.warn('getTotalPagesFromUploadedFiles: Actual page counts should be fetched from the server.');

  let totalPages = 0;
  if (userFiles?.files && Array.isArray(userFiles.files)) {
    userFiles.files.forEach((file) => {
      if (file.filename.toLowerCase().endsWith('.pdf')) {
        // Placeholder: Estimate pages (not recommended for production)
        const fileSizeMB = file.file_size / (1024 * 1024);
        const estimatedPages = Math.ceil(fileSizeMB * 2); // Rough estimate: 2 pages per MB
        totalPages += estimatedPages;
      }
    });
  }

  return totalPages;
};

// Function to get organization ID from user data
export const getUserOrganizationId = (userData) => {
  return userData?.organization_id || null;
};